import { Button } from "@/components/ui/button";
import {
  Alert,
  AlertDescription,
} from "@/components/ui/alert";
import {
  Loader2,
  AlertCircle,
  Search,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MODULE_WORKSPACE_X } from "@/components/module_summary/moduleWorkspaceLayout";
import { GrantDiscoverGrantCard } from "@/components/grant_suite/GrantDiscoverGrantCard";
import {
  GrantDiscoverActiveFilters,
  GrantDiscoverCardSkeletonGrid,
  GrantDiscoverEmptyResults,
  GrantDiscoverFiltersBar,
  GrantDiscoverHero,
  GrantDiscoverInsightsCallout,
  GrantDiscoverProfileSetupBanner,
  GrantDiscoverRecommendationsEmpty,
  GrantDiscoverResultsGrid,
  GrantDiscoverSectionHeader,
  GrantDiscoverStatusStack,
} from "@/components/grant_suite/GrantDiscoverViews";
import type { DiscoverGrant } from "@/components/grant_suite/grantDiscoverUi";
import { cn } from "@/lib/utils";
import { useUser } from "@/context/UserProvider";
import { useMunicipality } from "@/context/MunicipalityContext";
import { useGrantSuiteNavigation } from "@/context/GrantSuiteNavigationContext";
import { GrantAffinityProfileWizard } from "@/components/grant_suite/GrantAffinityProfileWizard";
import { useQueryClient } from "@tanstack/react-query";
import { useGrantDiscover } from "@/hooks/useGrantDiscover";
import { userHasGrantDiscover, userHasGrantSuite } from "@/lib/grantSuiteAccess";

type GrantDiscoverPanelProps = {
  onSavedToWatchlist?: () => void;
  profileWizardOpen?: boolean;
  onProfileWizardOpenChange?: (open: boolean) => void;
};

export function GrantDiscoverPanel({
  onSavedToWatchlist,
  profileWizardOpen: profileWizardOpenProp,
  onProfileWizardOpenChange,
}: GrantDiscoverPanelProps = {}) {
  const { currentUser } = useUser();
  const { municipality } = useMunicipality();
  const { navigateToGrantOpportunity } = useGrantSuiteNavigation();
  const municipalityId = municipality?.id;
  const canUseGrantDiscover = userHasGrantDiscover(currentUser) && Boolean(municipalityId);
  const canUseGrantPipeline = userHasGrantSuite(currentUser) && Boolean(municipalityId);

  const queryClient = useQueryClient();
  const discover = useGrantDiscover({
    municipalityId,
    canUseGrantDiscover,
    canUseGrantPipeline,
    onSavedToWatchlist,
    profileWizardOpen: profileWizardOpenProp,
    onProfileWizardOpenChange,
  });

  const canProceedFromStep1 = discover.query.trim().length > 0;
  const showEmbeddedSuggestions = canUseGrantDiscover && discover.profileComplete && !discover.hasSearched;
  const showSearchResults = discover.hasSearched;

  const renderGrantCard = (g: DiscoverGrant) => (
    <GrantDiscoverGrantCard
      key={g.id}
      grant={g}
      summarizing={discover.summarizingGrants[g.id]}
      summary={discover.grantSummaries[g.id]}
      error={discover.grantErrors[g.id]}
      onSummarizeEligibility={discover.handleSummarizeEligibility}
      showWatchlistButton={canUseGrantPipeline}
      onSaveToWatchlist={discover.handleSaveToWatchlist}
      onStartPursuing={discover.handleStartPursuing}
      watchlistSaving={!!discover.watchlistSaving[g.id]}
      pursuingSaving={!!discover.pursuingSaving[g.id]}
      onSaved={discover.savedGrantIds.has(g.id)}
      onPursuing={discover.pursuingGrantIds.has(g.id)}
      trackedOpportunityId={discover.pursuingOpportunityByGrantId.get(g.id)}
      onOpenInTracker={navigateToGrantOpportunity}
      trackingDisabled={canUseGrantPipeline && !municipalityId}
      fitTier={g.fit_tier}
      showExplainFit={canUseGrantDiscover && discover.profileComplete}
      fitExplanation={discover.fitExplanations[g.id]}
      explainingFit={!!discover.explainingFitGrants[g.id]}
      fitExplanationError={discover.fitExplanationErrors[g.id]}
      onExplainFit={discover.handleExplainFit}
    />
  );

  const statusBanners = [
    !municipalityId && {
      id: "municipality",
      tone: "warning" as const,
      message: "Select a municipality above to score results and save grants to your pipeline.",
    },
    discover.watchlistError && {
      id: "watchlist",
      tone: "error" as const,
      message: discover.watchlistError,
    },
    discover.profileAwaitingEmbed && {
      id: "embed",
      tone: "loading" as const,
      message: "Building fit scores from your profile…",
      action: municipalityId
        ? {
            label: "Refresh",
            onClick: () => {
              if (municipalityId) {
                queryClient.invalidateQueries({
                  queryKey: ["grant_affinity_profile", municipalityId],
                });
              }
            },
          }
        : undefined,
    },
  ].filter(Boolean) as {
    id: string;
    tone: "info" | "warning" | "error" | "loading";
    message: string;
    action?: { label: string; onClick: () => void };
  }[];

  const discoverFiltersBar = (
    <div className="space-y-3">
      <GrantDiscoverFiltersBar
        keyword={discover.keyword}
        onKeywordChange={discover.setKeyword}
        onSearchSubmit={discover.handleSearchSubmit}
        facetsLoading={discover.facetsLoading}
        facets={discover.facets}
        agency={discover.agency}
        onAgencyChange={discover.setAgency}
        catalogScope={discover.catalogScope}
        onCatalogScopeChange={discover.setCatalogScope}
        stateFilter={discover.stateFilter}
        onStateFilterChange={discover.setStateFilter}
        excludeFormula={discover.excludeFormula}
        onExcludeFormulaChange={discover.setExcludeFormula}
        sort={discover.sort}
        onSortChange={discover.setSort}
        sortOptions={discover.sortOptions}
        hasActiveFilters={discover.hasActiveFilters}
        onClearFilters={discover.clearFilters}
      />
      <GrantDiscoverActiveFilters
        profileScored={canUseGrantDiscover && discover.profileComplete}
        chips={discover.activeFilterChips}
      />
    </div>
  );

  const searchResultsPanel = (
    <div className="space-y-3">
      <GrantDiscoverSectionHeader
        title="Search results"
        description="Programs matching your description and filters."
        icon={Search}
        count={discover.totalCount}
      />
      {discover.loading ? (
        <GrantDiscoverCardSkeletonGrid />
      ) : discover.grants.length > 0 ? (
        <>
          <GrantDiscoverResultsGrid>{discover.grants.map((g) => renderGrantCard(g))}</GrantDiscoverResultsGrid>
          {discover.hasMore && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={discover.loadMore}
                disabled={discover.loadingMore}
                className="min-w-[120px]"
              >
                {discover.loadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Load more
              </Button>
            </div>
          )}
        </>
      ) : (
        <GrantDiscoverEmptyResults
          hasActiveFilters={discover.hasActiveFilters}
          onClearFilters={discover.clearFilters}
          onNewSearch={() => discover.setHasSearched(false)}
          homeState={discover.affinityProfile?.state ?? municipality?.state ?? null}
        />
      )}
    </div>
  );

  const recommendationsPanel = (
    <div className="space-y-3">
      <GrantDiscoverSectionHeader
        title="Recommended for you"
        description="Programs ranked against your municipality affinity profile."
        count={discover.recommendedTotal}
      />
      {discover.recommendationsInitialLoading ? (
        <GrantDiscoverCardSkeletonGrid />
      ) : discover.recommendationsQueryError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {discover.recommendationsQueryError instanceof Error
              ? discover.recommendationsQueryError.message
              : "Could not load suggestions."}
          </AlertDescription>
        </Alert>
      ) : discover.recommendedGrants.length > 0 ? (
        <>
          <GrantDiscoverResultsGrid>
            {discover.recommendedGrants.map((g) => renderGrantCard(g))}
          </GrantDiscoverResultsGrid>
          {discover.hasMoreRecommendations && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => discover.setRecommendedPage((p) => p + 1)}
                disabled={discover.recommendationsLoadingMore}
                className="min-w-[120px]"
              >
                {discover.recommendationsLoadingMore ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Load more
              </Button>
            </div>
          )}
        </>
      ) : (
        <GrantDiscoverRecommendationsEmpty profileAwaitingEmbed={discover.profileAwaitingEmbed} />
      )}
    </div>
  );

  const showMainScroll = showEmbeddedSuggestions || showSearchResults;

  return (
    <>
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col",
          showMainScroll ? "overflow-hidden" : "overflow-y-auto",
        )}
      >
        <div className={`${MODULE_WORKSPACE_X} shrink-0 space-y-3 pb-3 pt-4 sm:pt-5`}>
          <GrantDiscoverStatusStack banners={statusBanners} />
          {!discover.profileComplete && !discover.profileLoading && municipalityId && !discover.hasSearched ? (
            <GrantDiscoverProfileSetupBanner onSetupProfile={() => discover.setProfileWizardOpen(true)} />
          ) : null}
          <GrantDiscoverHero
            query={discover.query}
            onQueryChange={discover.setQuery}
            onSubmit={discover.handleSearchSubmit}
            loading={discover.loading}
            canSubmit={canProceedFromStep1}
            profileComplete={discover.profileComplete}
            compact={discover.hasSearched}
          />
          {!discover.hasSearched && discover.recommendedInsights ? (
            <GrantDiscoverInsightsCallout>{discover.recommendedInsights}</GrantDiscoverInsightsCallout>
          ) : null}
        </div>

        {showMainScroll ? (
          <div className="relative min-h-0 flex-1">
            <ScrollArea className="h-full min-h-0">
              <div className={`${MODULE_WORKSPACE_X} space-y-4 pb-6 pt-1`}>
                {discoverFiltersBar}
                {showEmbeddedSuggestions ? recommendationsPanel : null}
                {showSearchResults ? searchResultsPanel : null}
              </div>
            </ScrollArea>
          </div>
        ) : null}
      </div>

      {canUseGrantPipeline && municipalityId && (
        <GrantAffinityProfileWizard
          municipalityId={municipalityId}
          open={discover.profileWizardOpen}
          onOpenChange={discover.setProfileWizardOpen}
          onSaved={discover.handleProfileSaved}
        />
      )}
    </>
  );
}
